import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Building2,
  Search,
  Plus,
  MoreVertical,
  Monitor,
  Users,
  Edit2,
  Trash2,
  ExternalLink,
  Mail,
  Phone,
} from 'lucide-react'
import api from '@/lib/api'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'

interface Customer {
  id: string
  name: string
  email: string
  phone: string | null
  address: string | null
  plan: 'starter' | 'professional' | 'enterprise'
  deviceCount: number
  userCount: number
  status: 'active' | 'inactive' | 'suspended'
  createdAt: string
  logoUrl: string | null
}

export default function CustomersPage() {
  const [search, setSearch] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const currentUser = useAuthStore((state) => state.user)
  const isAdmin = currentUser?.role === 'admin'

  const { data: customers } = useQuery<Customer[]>({
    queryKey: ['customers'],
    queryFn: async () => {
      const response = await api.get('/customers')
      return response.data
    },
    placeholderData: [
      {
        id: '1',
        name: 'Speed Racing Center',
        email: 'contact@speedracing.com',
        phone: '+1 (555) 123-4567',
        address: '123 Racing Blvd, Miami, FL',
        plan: 'enterprise',
        deviceCount: 12,
        userCount: 5,
        status: 'active',
        createdAt: '2024-01-15T00:00:00Z',
        logoUrl: null,
      },
      {
        id: '2',
        name: 'Downtown Sim Lounge',
        email: 'info@simlounge.com',
        phone: '+1 (555) 987-6543',
        address: '456 Main St, New York, NY',
        plan: 'professional',
        deviceCount: 8,
        userCount: 3,
        status: 'active',
        createdAt: '2024-02-20T00:00:00Z',
        logoUrl: null,
      },
      {
        id: '3',
        name: 'ProSim Arena',
        email: 'support@prosimarena.com',
        phone: '+1 (555) 456-7890',
        address: '789 Tech Park, Austin, TX',
        plan: 'enterprise',
        deviceCount: 20,
        userCount: 8,
        status: 'active',
        createdAt: '2024-03-10T00:00:00Z',
        logoUrl: null,
      },
      {
        id: '4',
        name: 'Virtual Racing Hub',
        email: 'hello@vracinghub.com',
        phone: null,
        address: '321 Innovation Dr, Seattle, WA',
        plan: 'starter',
        deviceCount: 4,
        userCount: 2,
        status: 'inactive',
        createdAt: '2024-04-05T00:00:00Z',
        logoUrl: null,
      },
    ],
  })

  const createCustomerMutation = useMutation({
    mutationFn: async (payload: {
      name: string
      email: string
      phone?: string
      address?: string
      plan?: 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE'
    }) => {
      const response = await api.post('/customers', payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      toast.success('Customer created')
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to create customer')
    },
  })

  const updateCustomerMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: Record<string, unknown> }) => {
      const response = await api.patch(`/customers/${id}`, payload)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      toast.success('Customer updated')
      setSelectedCustomer(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to update customer')
    },
  })

  const deleteCustomerMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/customers/${id}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] })
      toast.success('Customer deleted')
      setSelectedCustomer(null)
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to delete customer')
    },
  })

  const filteredCustomers = customers?.filter((customer) => {
    if (search) {
      const searchLower = search.toLowerCase()
      return (
        customer.name.toLowerCase().includes(searchLower) ||
        customer.email.toLowerCase().includes(searchLower)
      )
    }
    return true
  })

  const getPlanBadge = (plan: string) => {
    switch (plan) {
      case 'enterprise':
        return (
          <span className="badge bg-sc-primary/10 text-sc-primary">
            Enterprise
          </span>
        )
      case 'professional':
        return (
          <span className="badge bg-sc-accent-cyan/10 text-sc-accent-cyan">
            Professional
          </span>
        )
      case 'starter':
        return (
          <span className="badge bg-sc-text-muted/10 text-sc-text-muted">
            Starter
          </span>
        )
      default:
        return null
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <span className="badge badge-online">Active</span>
      case 'inactive':
        return <span className="badge badge-offline">Inactive</span>
      case 'suspended':
        return (
          <span className="badge bg-sc-accent-red/10 text-sc-accent-red">
            Suspended
          </span>
        )
      default:
        return null
    }
  }

  const handleAddCustomer = () => {
    if (!isAdmin) {
      toast.error('Only admins can create customers')
      return
    }

    const name = window.prompt('Customer name')?.trim()
    if (!name) return

    const email = window.prompt('Customer contact email')?.trim()
    if (!email) return

    const phone = window.prompt('Customer phone (optional)')?.trim() || undefined
    const address = window.prompt('Customer address (optional)')?.trim() || undefined
    const planInput = window.prompt('Plan: starter, professional, or enterprise', 'starter')?.trim().toLowerCase()

    const plan = planInput === 'professional'
      ? 'PROFESSIONAL'
      : planInput === 'enterprise'
        ? 'ENTERPRISE'
        : 'STARTER'

    createCustomerMutation.mutate({ name, email, phone, address, plan })
  }

  const handleEditCustomer = (customer: Customer) => {
    const name = window.prompt('Customer name', customer.name)?.trim()
    if (!name) return

    const email = window.prompt('Customer contact email', customer.email)?.trim()
    if (!email) return

    const phone = window.prompt('Customer phone', customer.phone || '')?.trim() || null
    const address = window.prompt('Customer address', customer.address || '')?.trim() || null
    const planInput = window.prompt('Plan: starter, professional, or enterprise', customer.plan)?.trim().toLowerCase()
    const statusInput = window.prompt('Status: active, inactive, or suspended', customer.status)?.trim().toLowerCase()

    const plan = planInput === 'professional'
      ? 'PROFESSIONAL'
      : planInput === 'enterprise'
        ? 'ENTERPRISE'
        : 'STARTER'

    const status = statusInput === 'inactive'
      ? 'INACTIVE'
      : statusInput === 'suspended'
        ? 'SUSPENDED'
        : 'ACTIVE'

    updateCustomerMutation.mutate({
      id: customer.id,
      payload: { name, email, phone, address, plan, status },
    })
  }

  const handleDeleteCustomer = (customer: Customer) => {
    if (!isAdmin) {
      toast.error('Only admins can delete customers')
      return
    }

    if (!window.confirm(`Delete ${customer.name}? This cannot be undone.`)) {
      return
    }

    deleteCustomerMutation.mutate(customer.id)
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-sc-text-primary">Customers</h1>
          <p className="text-sc-text-muted mt-1">
            Manage your customer organizations
          </p>
        </div>
        <button className="btn-primary" onClick={handleAddCustomer}>
          <Plus className="w-4 h-4" />
          Add Customer
        </button>
      </div>

      {/* Search */}
      <div className="card">
        <div className="card-body py-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sc-text-muted" />
            <input
              type="text"
              placeholder="Search customers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>
      </div>

      {/* Customers grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filteredCustomers?.map((customer) => (
          <div key={customer.id} className="card hover:border-sc-border-medium transition-colors">
            <div className="card-body">
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-sc-primary/10 flex items-center justify-center">
                    {customer.logoUrl ? (
                      <img
                        src={customer.logoUrl}
                        alt={customer.name}
                        className="w-full h-full rounded-lg object-cover"
                      />
                    ) : (
                      <Building2 className="w-6 h-6 text-sc-primary" />
                    )}
                  </div>
                  <div>
                    <h3 className="font-semibold text-sc-text-primary">
                      {customer.name}
                    </h3>
                    <div className="flex items-center gap-2 mt-0.5">
                      {getPlanBadge(customer.plan)}
                      {getStatusBadge(customer.status)}
                    </div>
                  </div>
                </div>
                <div className="relative">
                  <button
                    onClick={() =>
                      setSelectedCustomer(
                        selectedCustomer === customer.id ? null : customer.id
                      )
                    }
                    className="p-2 text-sc-text-muted hover:text-sc-text-primary hover:bg-sc-bg-light rounded-lg"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>

                  {selectedCustomer === customer.id && (
                    <div className="absolute right-0 top-full mt-1 w-40 bg-sc-bg-medium border border-sc-border-medium rounded-lg shadow-sc-lg z-10 py-1">
                      <button
                        onClick={() => navigate(`/devices?customerId=${customer.id}`)}
                        className="w-full px-3 py-2 text-left text-sm text-sc-text-secondary hover:bg-sc-bg-light flex items-center gap-2"
                      >
                        <ExternalLink className="w-4 h-4" />
                        View Devices
                      </button>
                      <button
                        onClick={() => handleEditCustomer(customer)}
                        className="w-full px-3 py-2 text-left text-sm text-sc-text-secondary hover:bg-sc-bg-light flex items-center gap-2"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteCustomer(customer)}
                        className="w-full px-3 py-2 text-left text-sm text-sc-accent-red hover:bg-sc-bg-light flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 py-4 border-y border-sc-border-dark">
                <div className="flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-sc-text-muted" />
                  <span className="text-sm">
                    <span className="font-medium text-sc-text-primary">
                      {customer.deviceCount}
                    </span>{' '}
                    <span className="text-sc-text-muted">devices</span>
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-sc-text-muted" />
                  <span className="text-sm">
                    <span className="font-medium text-sc-text-primary">
                      {customer.userCount}
                    </span>{' '}
                    <span className="text-sc-text-muted">users</span>
                  </span>
                </div>
              </div>

              {/* Contact info */}
              <div className="pt-4 space-y-2">
                <a
                  href={`mailto:${customer.email}`}
                  className="flex items-center gap-2 text-sm text-sc-text-secondary hover:text-sc-primary transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  {customer.email}
                </a>
                {customer.phone && (
                  <a
                    href={`tel:${customer.phone}`}
                    className="flex items-center gap-2 text-sm text-sc-text-secondary hover:text-sc-primary transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                    {customer.phone}
                  </a>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-sc-border-dark">
                <button
                  className="btn-secondary flex-1 py-1.5 text-sm"
                  onClick={() => navigate(`/devices?customerId=${customer.id}`)}
                >
                  <Monitor className="w-4 h-4" />
                  Devices
                </button>
                <button
                  className="btn-primary flex-1 py-1.5 text-sm"
                  onClick={() => navigate(`/users?organizationId=${customer.id}`)}
                >
                  <Users className="w-4 h-4" />
                  Manage
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty state */}
      {filteredCustomers?.length === 0 && (
        <div className="card">
          <div className="card-body p-12 text-center">
            <Building2 className="w-12 h-12 text-sc-text-muted mx-auto mb-4" />
            <h3 className="text-lg font-medium text-sc-text-primary mb-2">
              No customers found
            </h3>
            <p className="text-sc-text-muted mb-4">
              {search
                ? 'Try adjusting your search terms'
                : 'Get started by adding your first customer'}
            </p>
            {!search && (
              <button className="btn-primary" onClick={handleAddCustomer}>
                <Plus className="w-4 h-4" />
                Add Customer
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
